import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, Instagram, Play } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

// Извлекаем shortcode из ссылки для построения превью
const getShortcode = (url: string) => {
  const m = url.match(/\/(?:p|reel|reels|tv)\/([^/?#]+)/);
  return m ? m[1] : '';
};
const getPreview = (url: string) => `/instagram-previews/${getShortcode(url)}.jpg`;

const LOOPS = 2;


const LazyEmbed: React.FC<{ url: string; shortcode: string; preview: string; isDraggingRef: React.MutableRefObject<boolean> }> = ({
  url,
  shortcode,
  preview,
  isDraggingRef,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [mount, setMount] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [interactive, setInteractive] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setMount(true);
          io.disconnect();
        }
      },
      { root: null, rootMargin: '600px 1200px', threshold: 0.01 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div ref={ref} className="relative mx-3 md:mx-4 mb-4 bg-[#F9F7F2] overflow-hidden h-[520px] md:h-[560px]">
      <img
        src={preview}
        alt=""
        loading="lazy"
        decoding="async"
        draggable={false}
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${loaded ? 'opacity-0' : 'opacity-100'}`}
      />
      {mount && (
        <iframe
          src={`https://www.instagram.com/p/${shortcode}/embed/captioned/`}
          title="Instagram post"
          loading="lazy"
          scrolling="no"
          allow="encrypted-media; autoplay"
          onLoad={() => setLoaded(true)}
          className={`absolute left-0 right-0 w-full bg-white ${interactive ? '' : 'pointer-events-none'}`}
          style={{ border: 0, top: '-54px', height: 'calc(100% + 110px)' }}
        />
      )}
      {!interactive && (
        <button
          type="button"
          aria-label="Play Instagram post"
          draggable={false}
          onDragStart={(e) => e.preventDefault()}
          onClick={(e) => {
            if (isDraggingRef.current) {
              e.preventDefault();
              return;
            }
            setInteractive(true);
          }}
          className="absolute inset-0 z-10 cursor-pointer group/play flex items-center justify-center bg-transparent"
        >
          <span className="w-16 h-16 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white opacity-0 group-hover/play:opacity-100 transition-opacity duration-300 shadow-xl">
            <Play size={26} className="ml-1" fill="currentColor" />
          </span>
        </button>
      )}
    </div>
  );
};

declare global {
  interface Window {
    instgrm?: { Embeds: { process: () => void } };
  }
}

export const InstagramReels: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasAdvanced, setHasAdvanced] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [activeCard, setActiveCard] = useState<number | null>(null);
  const draggingRef = useRef(false);
  const [reels, setReels] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('instagram_reels')
        .select('url, position')
        .order('position', { ascending: true });
      if (!cancelled && data) setReels(data.map((r: any) => r.url));
    })();
    return () => { cancelled = true; };
  }, []);

  const loopedReels = useMemo(
    () => Array.from({ length: LOOPS }, () => reels).flat(),
    [reels]
  );

  // Visibility observer for header animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.05 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Smooth drag-to-scroll with momentum (mouse + touch)
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    let isDown = false;
    let startX = 0;
    let startScroll = 0;
    let lastX = 0;
    let lastT = 0;
    let velocity = 0;
    let momentumId = 0;
    let pendingScroll = 0;
    let rafId = 0;

    const cancelMomentum = () => {
      if (momentumId) cancelAnimationFrame(momentumId);
      momentumId = 0;
    };

    const applyScroll = () => {
      el.scrollLeft = pendingScroll;
      rafId = 0;
    };

    const onDown = (e: PointerEvent) => {
      cancelMomentum();
      isDown = true;
      draggingRef.current = false;
      setIsDragging(false);
      startX = lastX = e.clientX;
      lastT = performance.now();
      startScroll = el.scrollLeft;
      velocity = 0;
    };
    const onMove = (e: PointerEvent) => {
      if (!isDown) return;
      const dx = e.clientX - startX;
      if (!draggingRef.current && Math.abs(dx) > 4) {
        draggingRef.current = true;
        setIsDragging(true);
        try { el.setPointerCapture(e.pointerId); } catch {}
      }
      if (!draggingRef.current) return;
      const now = performance.now();
      const dt = Math.max(1, now - lastT);
      velocity = (e.clientX - lastX) / dt; // px/ms
      lastX = e.clientX;
      lastT = now;
      pendingScroll = startScroll - dx;
      if (dx > 0) setHasAdvanced(true);
      if (!rafId) rafId = requestAnimationFrame(applyScroll);
    };
    const onUp = (e: PointerEvent) => {
      if (!isDown) return;
      isDown = false;
      try { el.releasePointerCapture(e.pointerId); } catch {}

      if (draggingRef.current && Math.abs(velocity) > 0.05) {
        // momentum: exponential decay
        let v = velocity * 16; // px per frame (~16ms)
        const friction = 0.94;
        const step = () => {
          v *= friction;
          el.scrollLeft -= v;
          if (Math.abs(v) > 0.4) {
            momentumId = requestAnimationFrame(step);
          } else {
            momentumId = 0;
          }
        };
        momentumId = requestAnimationFrame(step);
      }
      // keep isDragging true briefly so click handlers can suppress
      setTimeout(() => {
        draggingRef.current = false;
        setIsDragging(false);
      }, 60);
    };

    const onClickCapture = (e: MouseEvent) => {
      if (draggingRef.current) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', onUp);
    el.addEventListener('click', onClickCapture, true);
    return () => {
      cancelMomentum();
      if (rafId) cancelAnimationFrame(rafId);
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onUp);
      el.removeEventListener('click', onClickCapture, true);
    };
  }, []);

  const scrollByCard = (dir: 1 | -1) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector<HTMLElement>('[data-reel-card]');
    const step = card ? card.clientWidth + 24 : 360;
    el.scrollBy({ left: dir * step, behavior: 'smooth' });
    if (dir === -1) setHasAdvanced(true);
  };

  return (
    <section
      ref={sectionRef}
      className="relative w-full bg-[#3d1f17] text-[#EAE6D7] py-24 md:py-32 overflow-hidden"
    >
      {/* Smooth blend with previous section (Products ends in #412018) */}
      <div className="absolute inset-x-0 top-0 h-40 pointer-events-none bg-gradient-to-b from-[#412018] to-transparent" />
      {/* Smooth blend into next section (Appointment #2a1a1e) */}
      <div className="absolute inset-x-0 bottom-0 h-[55%] pointer-events-none bg-gradient-to-b from-transparent via-[#321a14] to-[#2a1a1e]" />
      <div className="absolute inset-0 opacity-[0.06] bg-[url('https://www.transparenttextures.com/patterns/cream-paper.png')] pointer-events-none" />
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_top,rgba(251,191,37,0.08),transparent_60%)]" />

      <div className="relative z-10">
        {/* Header */}
        <div className="container mx-auto px-6 mb-14 md:mb-20">
          <div className="text-center max-w-4xl mx-auto" dir="rtl">
            <span
              className={`inline-flex items-center gap-2 text-[#fbbf25] text-sm tracking-[0.3em] uppercase mb-6 transition-all duration-1000 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              <Instagram size={16} />
              אינסטגרם
            </span>
            <h2
              className={`text-5xl md:text-7xl lg:text-8xl font-serif text-[#EAE6D7] mb-6 leading-[1.1] tracking-wide transition-all duration-1000 delay-100 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              רגעים <span className="text-[#fbbf25] italic">מהקליניקה</span>
            </h2>
            <p
              className={`text-lg text-[#EAE6D7]/60 font-light leading-relaxed transition-all duration-1000 delay-200 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              הצצה לטיפולים, לפורמולות ולסיפורים מאחורי הקלעים — ישירות מהאינסטגרם שלנו.
            </p>
          </div>
        </div>

        {/* Carousel */}
        <div className="relative">
          <div
            ref={scrollerRef}
            dir="rtl"
            className={`flex gap-6 md:gap-8 overflow-x-auto pb-10 px-6 md:px-[max(1.5rem,calc((100vw-1280px)/2))] [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] select-none touch-pan-x overscroll-x-contain ${
              isDragging ? 'cursor-grabbing' : 'cursor-grab'
            }`}
            style={{ scrollBehavior: 'auto' }}
          >
            {loopedReels.map((url, index) => {
              const isActive = activeCard === index;
              return (
                <div
                  key={`${url}-${index}`}
                  data-reel-card
                  className={`shrink-0 w-[85vw] sm:w-[60vw] md:w-[400px] lg:w-[440px] transition-all duration-700 ease-out ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                  style={{ transitionDelay: `${Math.min(index, 8) * 80}ms` }}
                >
                  <div className="bg-white border border-white/10 overflow-hidden relative flex flex-col h-full shadow-xl shadow-black/40 hover:shadow-2xl hover:shadow-[#fbbf25]/20 transition-shadow duration-500 group">
                    {/* Eyebrow */}
                    <div className="flex items-center justify-between px-5 md:px-6 pt-5 md:pt-6 pb-3" dir="rtl">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#67392b] to-[#412018] flex items-center justify-center text-[#fbbf25] shrink-0">
                          <Instagram size={14} />
                        </div>
                        <div className="flex flex-col leading-tight">
                          <span className="text-[11px] uppercase tracking-[0.25em] text-[#412018] font-bold">
                            riham_clinic
                          </span>
                          <span className="text-[10px] uppercase tracking-[0.2em] text-[#412018]/40">
                            Instagram
                          </span>
                        </div>
                      </div>
                      <a
                        href={url}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => { if (isDragging) e.preventDefault(); }}
                        className="text-[10px] uppercase tracking-[0.2em] text-[#67392b] hover:text-[#412018] transition-colors"
                      >
                        פתח ↗
                      </a>
                    </div>

                    {/* Instagram embed (lazy) */}
                    <LazyEmbed url={url} shortcode={getShortcode(url)} preview={getPreview(url)} isDraggingRef={draggingRef} />

                  </div>
                </div>
              );
            })}
          </div>

          {/* Side arrows */}
          <button
            aria-label="Previous"
            onClick={() => scrollByCard(1)}
            className={`hidden md:flex absolute right-4 lg:right-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/5 backdrop-blur border border-white/15 items-center justify-center text-[#EAE6D7] hover:bg-[#fbbf25] hover:text-[#412018] hover:border-[#fbbf25] transition-all duration-500 shadow-lg shadow-black/40 ${
              hasAdvanced ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
          >
            <ArrowRight size={18} />
          </button>
          <button
            aria-label="Next"
            onClick={() => scrollByCard(-1)}
            className="hidden md:flex absolute left-4 lg:left-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/5 backdrop-blur border border-white/15 items-center justify-center text-[#EAE6D7] hover:bg-[#fbbf25] hover:text-[#412018] hover:border-[#fbbf25] transition-all duration-300 shadow-lg shadow-black/40"
          >
            <ArrowLeft size={18} />
          </button>
        </div>
      </div>
    </section>
  );
};
